import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

const POS_FEATURES = [
    {
        icon: "mdi:barcode-scan",
        title: "Leitor de código de barras",
        description: "Escaneie produtos e adicione ao carrinho instantaneamente.",
    },
    {
        icon: "mdi:lightning-bolt-outline",
        title: "Vendas rápidas",
        description: "Interface otimizada para vendas em segundos, sem complicações.",
    },
    {
        icon: "mdi:receipt-text-outline",
        title: "Facturação integrada",
        description: "Emite facturas, recibos e documentos fiscais automaticamente.",
    },
    {
        icon: "mdi:silverware-fork-knife",
        title: "Modo restaurante",
        description: "Gestão de mesas, pedidos e cozinha com ecrã dedicado.",
    },
    {
        icon: "mdi:account-cash-outline",
        title: "Gestão de clientes",
        description: "Regista clientes e acompanha o histórico de compras.",
    },
    {
        icon: "mdi:chart-timeline-variant",
        title: "Relatórios em tempo real",
        description: "Vendas, produtos mais vendidos e desempenho por caixa.",
    },
];

export default function POSSection() {
    return (
        <section className="py-20 lg:py-28 bg-linear-to-br from-blue-950 via-slate-900 to-slate-900 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-px w-8 bg-blue-500" />
                            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">
                                Ponto de Venda
                            </span>
                        </div>

                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                            POS integrado para{" "}
                            <span className="text-blue-400">vendas presenciais</span>
                        </h2>

                        <p className="text-white/50 mb-8 leading-relaxed">
                            O Logigate POS transforma o teu computador ou tablet num ponto de venda completo.
                            Ideal para supermercados, restaurantes, farmácias e qualquer comércio.
                        </p>

                        {/* Feature grid */}
                        <div className="grid sm:grid-cols-2 gap-4">
                            {POS_FEATURES.map((feature, index) => (
                                <motion.div
                                    key={feature.title}
                                    initial={{ opacity: 0, y: 15 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.3, delay: 0.2 + index * 0.06 }}
                                    className="flex gap-3"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                        <Icon icon={feature.icon} width={20} className="text-blue-400" />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-white mb-0.5">
                                            {feature.title}
                                        </h4>
                                        <p className="text-xs text-white/40 leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Right - Visual mockup */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                    >
                        <div className="relative">
                            {/* POS Interface mockup */}
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 shadow-2xl">
                                {/* Top bar */}
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-red-400" />
                                        <div className="w-3 h-3 rounded-full bg-yellow-400" />
                                        <div className="w-3 h-3 rounded-full bg-blue-400" />
                                    </div>
                                    <span className="text-xs text-white/30">Logigate POS</span>
                                </div>

                                <div className="grid grid-cols-5 gap-3">
                                    {/* Products grid (left) */}
                                    <div className="col-span-3 space-y-2">
                                        <div className="bg-white/5 rounded-lg p-2 flex items-center gap-2">
                                            <Icon icon="mdi:magnify" width={16} className="text-white/30" />
                                            <span className="text-xs text-white/30">Pesquisar produto...</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { name: "Arroz 1kg", price: "450" },
                                                { name: "Óleo 1L", price: "890" },
                                                { name: "Açúcar 1kg", price: "380" },
                                                { name: "Leite 1L", price: "650" },
                                                { name: "Farinha 1kg", price: "420" },
                                                { name: "Massa 500g", price: "350" },
                                            ].map((item) => (
                                                <div
                                                    key={item.name}
                                                    className="bg-white/5 hover:bg-white/10 rounded-lg p-2 text-center transition-colors cursor-pointer"
                                                >
                                                    <div className="w-8 h-8 mx-auto mb-1 rounded bg-white/10 flex items-center justify-center">
                                                        <Icon icon="mdi:package-variant-closed" width={14} className="text-white/40" />
                                                    </div>
                                                    <div className="text-[10px] text-white/70 truncate">{item.name}</div>
                                                    <div className="text-[10px] text-blue-400 font-medium">{item.price} Kz</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Cart (right) */}
                                    <div className="col-span-2 bg-white/5 rounded-lg p-3">
                                        <div className="text-xs text-white/50 font-medium mb-2">Carrinho</div>
                                        <div className="space-y-1.5">
                                            {[
                                                { name: "Arroz 1kg", qty: 2, price: "900" },
                                                { name: "Óleo 1L", qty: 1, price: "890" },
                                                { name: "Leite 1L", qty: 3, price: "1.950" },
                                            ].map((item) => (
                                                <div key={item.name} className="flex justify-between text-[10px]">
                                                    <span className="text-white/60">{item.qty}x {item.name}</span>
                                                    <span className="text-white/80">{item.price}</span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3 pt-2 border-t border-white/10">
                                            <div className="flex justify-between">
                                                <span className="text-xs text-white/50">Total</span>
                                                <span className="text-sm font-bold text-blue-400">3.740 Kz</span>
                                            </div>
                                        </div>
                                        <div className="mt-2 bg-blue-500 rounded-lg py-1.5 text-center text-xs font-medium text-white">
                                            Finalizar venda
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Floating badges */}
                            <motion.div
                                className="absolute -top-3 -left-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, -6, 0] }}
                                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:barcode-scan" width={16} className="text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-700">Código de barras</span>
                                </div>
                            </motion.div>

                            <motion.div
                                className="absolute -bottom-3 -right-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, 6, 0] }}
                                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon icon="mdi:printer-pos" width={16} className="text-blue-500" />
                                    <span className="text-xs font-semibold text-gray-700">Impressão directa</span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
